import { create } from 'zustand'
import { apiClient } from '@/lib/api'
import type { AdminUser } from '@/types'

interface LoginCredentials {
  username: string
  password: string
}

interface AuthState {
  user: AdminUser | null
  loading: boolean
  error: string | null
  login: (credentials: LoginCredentials) => Promise<{ success: boolean; user?: AdminUser; error?: string }>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  setUser: (user: AdminUser | null) => void
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: true,
  error: null,

  setUser: (user) => {
    if (typeof window !== 'undefined') {
      if (user) {
        localStorage.setItem('admin_user', JSON.stringify(user))
      } else {
        localStorage.removeItem('admin_user')
      }
    }
    set({ user })
  },

  login: async (credentials) => {
    set({ loading: true, error: null })

    try {
      console.log('开始登录请求:', credentials)
      const response = await apiClient.post<{
        admin: AdminUser
        access_token: string
        refresh_token: string
      }>('/admin-api/v1/admin/auth/login', credentials)

      console.log('登录响应:', response)

      if (response.code === 0) {
        const { admin, access_token } = response.data
        console.log('登录成功,设置token:', access_token.substring(0, 20) + '...')
        apiClient.setToken(access_token)
        // 保存用户信息到localStorage
        if (typeof window !== 'undefined') {
          localStorage.setItem('admin_user', JSON.stringify(admin))
        }
        set({ user: admin, loading: false, error: null })
        return { success: true, user: admin }
      } else {
        console.log('登录失败,响应code不为0:', response)
        throw new Error(response.message)
      }
    } catch (error: any) {
      console.error('登录异常:', error)
      const errorMessage = error.message || '登录失败'
      set({ loading: false, error: errorMessage })
      return { success: false, error: errorMessage }
    }
  },

  logout: async () => {
    try {
      await apiClient.post('/admin-api/v1/admin/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      apiClient.clearToken()
      // 清除用户信息
      if (typeof window !== 'undefined') {
        localStorage.removeItem('admin_user')
      }
      set({ user: null, loading: false, error: null })
    }
  },

  checkAuth: async () => {
    const token = apiClient.getToken()
    if (!token) {
      set({ loading: false, user: null, error: null })
      return
    }

    try {
      const response = await apiClient.get<AdminUser>('/admin-api/v1/admin/auth/profile')
      if (response.code === 0) {
        const user = response.data
        if (typeof window !== 'undefined') {
          localStorage.setItem('admin_user', JSON.stringify(user))
        }
        set({ user, loading: false, error: null })
        return
      }
      throw new Error(response.message || '获取管理员信息失败')
    } catch (error) {
      console.error('获取管理员信息失败:', error)
      if (typeof window !== 'undefined') {
        const userStr = localStorage.getItem('admin_user')
        if (userStr) {
          try {
            const user = JSON.parse(userStr) as AdminUser
            set({ user, loading: false, error: null })
            return
          } catch (parseError) {
            console.error('解析用户信息失败:', parseError)
          }
        }
      }
      apiClient.clearToken()
      set({ loading: false, user: null, error: '登录状态已失效' })
    }
  },
}))
